import React, { useState } from 'react';
import { Users, UserPlus, CheckCircle, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, PageHeader } from '../components/Card';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Collaboration() {
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Partner');
  const [emailError, setEmailError] = useState('');
  const { collaborators, setCollaborators, showToast } = useApp();

  const isValidEmail = EMAIL_REGEX.test(email.trim());

  const handleInvite = () => {
    if (!email.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    if (!isValidEmail) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setCollaborators(prev => [...prev, {
      id: Date.now().toString(),
      email: email.trim(),
      role,
      status: 'Pending',
      date: new Date().toISOString(),
    }]);
    setEmail('');
    setEmailError('');
    setShowInvite(false);
    showToast('Invitation sent!');
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  return (
    <div>
      <PageHeader
        icon={<Users size={22} />}
        title="Collaboration"
        subtitle="Share your home buying journey with your partner, agent, and mortgage broker"
        action={
          <button onClick={() => setShowInvite(true)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 'var(--radius-sm)',
            background: 'var(--primary)', color: 'white',
            fontWeight: 600, fontSize: 14,
          }}>
            <UserPlus size={16} /> Invite Collaborator
          </button>
        }
      />

      {showInvite && (
        <Card style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Invite a Collaborator</h3>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Email Address</label>
              <input type="email" value={email} onChange={handleEmailChange}
                placeholder="partner@example.com"
                style={{
                  width: '100%', padding: '10px 14px',
                  border: `1px solid ${emailError ? 'var(--red)' : 'var(--gray-300)'}`,
                  borderRadius: 'var(--radius-sm)', fontSize: 14,
                }} />
              {emailError && (
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>{emailError}</div>
              )}
            </div>
            <div style={{ minWidth: 140 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role</label>
              <select value={role} onChange={e => setRole(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px',
                  border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 14, background: 'white',
                }}>
                <option>Partner</option>
                <option>Real Estate Agent</option>
                <option>Mortgage Broker</option>
                <option>Lawyer</option>
                <option>Family Member</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end', paddingBottom: emailError ? 20 : 0 }}>
              <button onClick={handleInvite} disabled={!email.trim() || !isValidEmail} style={{
                padding: '10px 20px',
                background: email.trim() && isValidEmail ? 'var(--primary)' : 'var(--gray-300)',
                color: 'white',
                borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14, height: 42,
                cursor: email.trim() && isValidEmail ? 'pointer' : 'default',
              }}>Send</button>
              <button onClick={() => { setShowInvite(false); setEmailError(''); setEmail(''); }} style={{
                padding: '10px 16px', background: 'var(--gray-200)',
                borderRadius: 'var(--radius-sm)', fontSize: 14, height: 42,
              }}>Cancel</button>
            </div>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Users size={20} color="var(--gray-500)" />
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Your Collaborators</h3>
        </div>

        {collaborators.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Users size={48} color="var(--gray-300)" style={{ marginBottom: 12 }} />
            <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 6 }}>
              No collaborators yet
            </h4>
            <p style={{ color: 'var(--gray-400)', fontSize: 14 }}>
              Invite your partner, agent, or mortgage broker to collaborate on your home buying journey
            </p>
          </div>
        ) : (
          <div>
            {collaborators.map(c => (
              <div key={c.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 0', borderBottom: '1px solid var(--gray-100)',
              }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{c.email}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{c.role} &middot; {c.status}</div>
                </div>
                <button
                  onClick={() => setCollaborators(prev => prev.filter(x => x.id !== c.id))}
                  style={{ background: 'none', color: 'var(--gray-400)', padding: 4 }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>How Collaboration Works</h3>
        {[
          'Collaborators receive an email invite and create their own account',
          'They can view your entire home buying journey and progress',
          'They can add comments and notes on any page to provide guidance',
          'They cannot edit or modify your data - comments only',
          'You can remove collaborators at any time',
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
            <CheckCircle size={18} color="var(--green)" />
            <span style={{ fontSize: 14 }}>{item}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
